import React, { ReactElement, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Immutable from 'immutable';

import SheetContainer, { Content, Overlay, Sheet } from '../../../../../../mobile/structural/sheet';
import { UseSheetReturnType } from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import { getSheetState } from '../../../../../../mobile/structural/sheet/sheetSelectors';
import CustomColorInput from '../../colorTools/CustomColorInput';
import ColorPopupContent from '../../colorTools/popup/ColorPopupContent';
import currentBoardSuggestedColorSelectorOnlyColors from '../../colorTools/currentBoardSuggestedColorsSelector';
import { COLOR_POPUP_MODE } from '../../colorTools/popup/colorPopupConstants';
import { Label } from '../../../../../../../common/labels/labelTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { COLORS } from '../../../../../../../common/colors/colorConstants';
import { getColorDefaultHexValue } from '../../../../../../../common/colors/elementColorFormatUtil';
import { updateLabel } from '../../../../../../../common/labels/labelActions';
import { SheetState } from '../../../../../../mobile/structural/sheet/sheetTypes';

import '../../colorTools/popup/mobile/MobileColorToolContent.scss';

type LabelColorSheetProps = {
    label: Label;
    sheetProps: UseSheetReturnType;
};

const LabelColorSheet = (props: LabelColorSheetProps): ReactElement => {
    const { label, sheetProps } = props;
    const dispatch = useDispatch();
    const customColorInputRef = useRef<CustomColorInput>(null);
    const suggestedColors = useSelector(currentBoardSuggestedColorSelectorOnlyColors);
    const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
    const [previousSheetStateForReopen, setPreviousSheetStateForReopen] = useState<SheetState | undefined>(undefined);
    const currentSheetState = useSelector(getSheetState(sheetProps.sheetId));

    const setColor = useCallback(
        (color: string | null) => {
            if (!color) return;

            const colorToSave = getColorDefaultHexValue(color);
            dispatch(updateLabel(label.id, { color: colorToSave }));
        },
        [dispatch, label.id],
    );

    const openCustomColorPicker = useCallback(() => {
        customColorInputRef.current?.open();
    }, []);

    const onCustomInputBlur = () => {
        sheetProps.dispatchOpenSheet(
            previousSheetStateForReopen ? { previousSheetState: previousSheetStateForReopen } : undefined,
        );
        setPreviousSheetStateForReopen(undefined);
        setIsCustomPickerOpen(false);
    };

    const onCustomInputFocus = () => {
        setPreviousSheetStateForReopen(currentSheetState?.previousSheetState);
        sheetProps.dispatchCloseSheet({ skipPreviousSheetRestore: true });
        setIsCustomPickerOpen(true);
    };

    const showCustomColorPickerInput = sheetProps.isSheetOpen || isCustomPickerOpen;

    return (
        <>
            <SheetContainer className="LabelColorSheet color-tool" {...sheetProps}>
                <Overlay />
                <Sheet>
                    <Content>
                        <ColorPopupContent
                            supportBackgroundColor={false}
                            supportColorBar={false}
                            setColor={setColor}
                            selectedColor={label.color}
                            colorPopupMode={COLOR_POPUP_MODE.COLOR}
                            defaultColors={Object.values(COLORS)}
                            suggestedColors={suggestedColors}
                            openCustomColorPicker={openCustomColorPicker}
                            supportDualColors={false}
                            selectedElements={Immutable.List<ImMNElement>()}
                        />
                    </Content>
                </Sheet>
            </SheetContainer>
            {showCustomColorPickerInput && (
                <CustomColorInput
                    className="custom-color-picker-input absolute top-0 left-0 opacity-0"
                    ref={customColorInputRef}
                    initialColor={getColorDefaultHexValue(label.color) || label.color}
                    setColor={setColor}
                    onBlur={onCustomInputBlur}
                    onFocus={onCustomInputFocus}
                />
            )}
        </>
    );
};

export default LabelColorSheet;
